import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import api from '@/lib/api';
import {
  LayoutDashboard, Package, FolderOpen, ClipboardList, FileText,
  Mail, Settings, LogOut, Menu, X, Image, Scale, Users, Truck
} from 'lucide-react';

const ALL_NAV_ITEMS = [
  { path: '/admin/dashboard', label: 'Dashboard', icon: LayoutDashboard, adminOnly: true },
  { path: '/admin/produkte', label: 'Produkte', icon: Package, adminOnly: true },
  { path: '/admin/kategorien', label: 'Kategorien', icon: FolderOpen, adminOnly: true },
  { path: '/admin/bestellungen', label: 'Bestellungen', icon: ClipboardList, adminOnly: true },
  { path: '/admin/lieferanten', label: 'Lieferanten', icon: Truck, adminOnly: true },
  { path: '/admin/cms', label: 'CMS', icon: FileText, adminOnly: false },
  { path: '/admin/rechtliches', label: 'Rechtliches', icon: Scale, adminOnly: false },
  { path: '/admin/email-vorlagen', label: 'E-Mail Vorlagen', icon: Mail, adminOnly: true },
  { path: '/admin/medien', label: 'Medien', icon: Image, adminOnly: true },
  { path: '/admin/benutzer', label: 'Benutzer', icon: Users, adminOnly: true },
  { path: '/admin/einstellungen', label: 'Einstellungen', icon: Settings, adminOnly: true },
];

export const AdminLayout = ({ children }) => {
  const { admin, logout, mustChangePassword, passwordChanged } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [changePwOpen, setChangePwOpen] = useState(mustChangePassword);
  const [currentPw, setCurrentPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [appVersion, setAppVersion] = useState('');

  const navItems = admin?.role === 'admin'
    ? ALL_NAV_ITEMS
    : ALL_NAV_ITEMS.filter(item => !item.adminOnly);

  useEffect(() => {
    api.get('/site-settings').then(r => setAppVersion(r.data.app_version || '')).catch(() => {});
  }, []);

  const handleChangePw = async () => {
    if (newPw !== confirmPw) { toast.error('Passwörter stimmen nicht überein'); return; }
    if (newPw.length < 8) { toast.error('Mindestens 8 Zeichen'); return; }
    try {
      await api.post('/auth/change-password', { current_password: currentPw, new_password: newPw });
      toast.success('Passwort geändert');
      setChangePwOpen(false);
      passwordChanged();
      setCurrentPw(''); setNewPw(''); setConfirmPw('');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler');
    }
  };

  const handleLogout = () => {
    logout();
    window.location.href = '/admin/login';
  };

  return (
    <div className="min-h-screen bg-[#F7F9FC] flex">
      {/* Sidebar Desktop */}
      <aside className="hidden lg:flex lg:flex-col w-64 bg-white border-r border-slate-200 fixed h-full" data-testid="admin-sidebar">
        <div className="p-6 border-b border-slate-100">
          <Link to="/admin/dashboard" className="text-lg font-bold text-[#0E518D]">GB Sylt Admin</Link>
          <p className="text-xs text-slate-400 mt-1">{admin?.email}</p>
        </div>
        <nav className="flex-1 p-4 space-y-1">
          {navItems.map(item => {
            const Icon = item.icon;
            const active = location.pathname === item.path;
            return (
              <Link key={item.path} to={item.path}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm font-medium transition-colors ${active ? 'bg-[#0E518D] text-white' : 'text-slate-600 hover:bg-slate-50 hover:text-[#0E518D]'}`}
                data-testid={`nav-${item.path.split('/').pop()}`}
              >
                <Icon className="h-4 w-4" />{item.label}
              </Link>
            );
          })}
        </nav>
        <div className="p-4 border-t border-slate-100">
          <p className="text-xs text-slate-400 px-3 mb-2" data-testid="sidebar-version">{appVersion}</p>
          <button onClick={handleLogout} className="flex items-center gap-3 px-3 py-2.5 text-sm text-slate-600 hover:text-red-500 w-full transition-colors" data-testid="admin-logout">
            <LogOut className="h-4 w-4" />Abmelden
          </button>
        </div>
      </aside>

      {/* Mobile Sidebar */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/50" onClick={() => setSidebarOpen(false)} />
          <aside className="absolute left-0 top-0 h-full w-64 bg-white z-50 shadow-lg">
            <div className="p-6 border-b border-slate-100 flex justify-between items-center">
              <span className="text-lg font-bold text-[#0E518D]">GB Sylt Admin</span>
              <button onClick={() => setSidebarOpen(false)}><X className="h-5 w-5" /></button>
            </div>
            <nav className="p-4 space-y-1">
              {navItems.map(item => {
                const Icon = item.icon;
                const active = location.pathname === item.path;
                return (
                  <Link key={item.path} to={item.path} onClick={() => setSidebarOpen(false)}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm font-medium transition-colors ${active ? 'bg-[#0E518D] text-white' : 'text-slate-600 hover:bg-slate-50'}`}
                  >
                    <Icon className="h-4 w-4" />{item.label}
                  </Link>
                );
              })}
            </nav>
            <div className="p-4 border-t border-slate-100">
              <button onClick={handleLogout} className="flex items-center gap-3 px-3 py-2.5 text-sm text-slate-600 hover:text-red-500 w-full">
                <LogOut className="h-4 w-4" />Abmelden
              </button>
            </div>
          </aside>
        </div>
      )}

      {/* Main Content */}
      <div className="flex-1 lg:ml-64">
        <header className="bg-white border-b border-slate-200 px-4 sm:px-6 py-4 flex items-center justify-between lg:justify-end">
          <button className="lg:hidden" onClick={() => setSidebarOpen(true)} data-testid="mobile-sidebar-toggle">
            <Menu className="h-5 w-5 text-slate-600" />
          </button>
          <div className="flex items-center gap-3">
            <Button variant="outline" size="sm" className="text-xs rounded-sm" onClick={() => setChangePwOpen(true)} data-testid="change-password-btn">
              Passwort ändern
            </Button>
          </div>
        </header>
        <main className="p-4 sm:p-6 lg:p-8">{children}</main>
      </div>

      {/* Password Change Dialog */}
      <Dialog open={changePwOpen} onOpenChange={mustChangePassword ? undefined : setChangePwOpen}>
        <DialogContent className="bg-white" data-testid="change-password-dialog" aria-describedby="pw-change-desc">
          <DialogHeader>
            <DialogTitle className="text-[#0E518D]">
              {mustChangePassword ? 'Passwort muss geändert werden' : 'Passwort ändern'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4" id="pw-change-desc">
            <div>
              <Label>Aktuelles Passwort</Label>
              <Input type="password" value={currentPw} onChange={e => setCurrentPw(e.target.value)} data-testid="current-password-input" />
            </div>
            <div>
              <Label>Neues Passwort</Label>
              <Input type="password" value={newPw} onChange={e => setNewPw(e.target.value)} data-testid="new-password-input" />
            </div>
            <div>
              <Label>Passwort bestätigen</Label>
              <Input type="password" value={confirmPw} onChange={e => setConfirmPw(e.target.value)} data-testid="confirm-password-input" />
            </div>
            <Button onClick={handleChangePw} className="w-full bg-[#0E518D] hover:bg-[#0B4171] text-white rounded-sm uppercase tracking-wide" data-testid="submit-password-change">
              Passwort ändern
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
